import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { api, money, formatError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { SpendNudge } from "@/components/SpendNudge";

const field = "w-full bg-[#F5F8F6] rounded-[14px] px-4 py-3 text-[#122117] placeholder-[#6B8375] focus:outline-none focus:ring-2 focus:ring-[#0B7A3B] border-none";

export default function Checkout() {
  const { items, setQty, removeItem, subtotal, charge, chargeLabel, grandTotal } = useCart();
  const { user, refresh } = useAuth();
  const reward = !!user?.reward_available;
  const dispCharge = reward ? 0 : charge;
  const dispLabel = reward ? "Service charge waived (loyalty reward)" : chargeLabel;
  const dispTotal = Math.round((subtotal + dispCharge) * 100) / 100;
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", address: "", postcode: "", delivery_zone: "", notes: "" });
  const [placing, setPlacing] = useState(false);
  const [soldOut, setSoldOut] = useState([]);

  useEffect(() => {
    if (user) setForm((f) => ({
      ...f,
      name: user.name || "",
      phone: user.phone || "",
      address: user.address || "",
      postcode: user.postcode || "",
      delivery_zone: user.delivery_zone || "",
    }));
  }, [user]);

  // Low-stock guard: verify availability on load
  useEffect(() => {
    (async () => {
      if (items.length === 0) return;
      try {
        const { data } = await api.get("/products");
        const map = Object.fromEntries(data.map((p) => [p.product_id, p]));
        const gone = items.filter((i) => !map[i.product.product_id]?.available).map((i) => i.product);
        setSoldOut(gone);
      } catch (e) {
        console.error("Checkout availability check failed", e);
      }
    })();
  }, [items.length]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (items.length === 0 && !placing) navigate("/cart", { replace: true });
  }, [items.length, placing, navigate]);

  const place = async () => {
    if (soldOut.length > 0) {
      toast.error("Please remove sold-out items first");
      return;
    }
    if (!form.name || !form.phone || !form.address || !form.postcode) {
      toast.error("Please fill in your delivery details");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        ...form,
        items: items.map((i) => ({ product_id: i.product.product_id, quantity: i.quantity })),
      });
      localStorage.setItem("pm_cart", "[]");
      await refresh();
      toast.success("Order placed! 🎉");
      window.dispatchEvent(new Event("storage"));
      navigate(`/order-success/${data.order_id}`, { replace: true, state: { order: data } });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.unavailable) {
        toast.error("Some items just sold out");
        const { data } = await api.get("/products");
        const map = Object.fromEntries(data.map((p) => [p.product_id, p]));
        setSoldOut(items.filter((i) => !map[i.product.product_id]?.available).map((i) => i.product));
      } else {
        toast.error(formatError(detail) || "Could not place order");
      }
      setPlacing(false);
    }
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#F5F8F6] pb-32">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-[#F5F8F6] px-4 py-3 pt-safe flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#F5F8F6] flex items-center justify-center active:scale-90" data-testid="checkout-back">
          <ArrowLeft size={20} className="text-[#122117]" />
        </button>
        <h1 className="text-xl font-extrabold text-[#122117]">Checkout</h1>
      </header>

      <div className="px-4 pt-4 space-y-4">
        <SpendNudge subtotal={subtotal} />

        {reward && (
          <div className="rounded-[16px] bg-[#0B7A3B] text-white px-4 py-3 flex items-center gap-2" data-testid="loyalty-reward-banner">
            <span className="text-lg leading-none">&#127873;</span>
            <span className="font-bold text-sm">Loyalty reward applied — your service charge is waived on this order!</span>
          </div>
        )}

        {soldOut.length > 0 && (
          <div className="rounded-[16px] bg-[#C0392B]/10 border border-[#C0392B]/30 p-4" data-testid="lowstock-guard">
            <div className="flex items-center gap-2 text-[#C0392B] font-bold mb-2">
              <AlertTriangle size={18} /> These items just sold out
            </div>
            <div className="space-y-2">
              {soldOut.map((p) => (
                <div key={p.product_id} className="flex items-center justify-between text-sm">
                  <span className="text-[#122117] font-semibold">{p.name}</span>
                  <button onClick={() => { removeItem(p.product_id); setSoldOut((s) => s.filter((x) => x.product_id !== p.product_id)); }} className="text-[#C0392B] font-bold underline" data-testid={`remove-soldout-${p.product_id}`}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Delivery form */}
        <div className="bg-white rounded-[20px] p-4 space-y-3">
          <h2 className="font-extrabold text-[#122117]">Delivery details</h2>
          <input className={field} placeholder="Full name" value={form.name} onChange={set("name")} data-testid="co-name" />
          <input className={field} placeholder="Phone number" value={form.phone} onChange={set("phone")} data-testid="co-phone" />
          <input className={field} placeholder="Delivery address" value={form.address} onChange={set("address")} data-testid="co-address" />
          <div className="grid grid-cols-2 gap-3">
            <input className={field} placeholder="Postcode (e.g. E1 6AN)" value={form.postcode} onChange={set("postcode")} data-testid="co-postcode" />
            <input className={field} placeholder="Zone (e.g. East London)" value={form.delivery_zone} onChange={set("delivery_zone")} data-testid="co-zone" />
          </div>
          <textarea className={field} placeholder="Delivery notes (optional)" rows={2} value={form.notes} onChange={set("notes")} data-testid="co-notes" />
        </div>

        {/* Summary */}
        <div className="bg-white rounded-[20px] p-4">
          <h2 className="font-extrabold text-[#122117] mb-3">Order summary</h2>
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.product.product_id} className="flex items-center justify-between text-sm">
                <span className="text-[#122117]">{it.product.name} × {it.quantity}</span>
                <span className="font-semibold text-[#122117]">{money(it.product.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="h-px bg-[#F5F8F6] my-3" />
          <div className="flex justify-between text-sm text-[#6B8375]"><span>Subtotal</span><span>{money(subtotal)}</span></div>
          <div className="flex justify-between text-sm text-[#6B8375] mt-1"><span>{dispLabel}</span><span>{money(dispCharge)}</span></div>
          <div className="flex justify-between font-extrabold text-lg text-[#122117] mt-2"><span>Total</span><span className="text-[#0B7A3B]">{money(dispTotal)}</span></div>
        </div>

        <p className="text-xs text-[#6B8375] text-center px-4">
          Payment is by bank transfer. Bank details will be shown after you place your order.
        </p>
      </div>

      <div className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[#F5F8F6] p-4 pb-safe max-w-md mx-auto">
        <button onClick={place} disabled={placing} className="w-full bg-[#0B7A3B] text-white font-extrabold rounded-full py-4 active:scale-95 transition-transform hover:bg-[#095F2E] disabled:opacity-60" data-testid="place-order">
          {placing ? "Placing order…" : `Place Order · ${money(dispTotal)}`}
        </button>
      </div>
    </div>
  );
}
